import { tokens, Types as ThemeTypes } from '@fiverr-private/theme';
import { BASE_BUTTON_COLORS, BASE_BUTTON_DISABLED_COLORS } from './BaseButton.constants';
import { BaseButtonProps } from './BaseButton.types';
import * as classes from './BaseButton.vs.css';

const getBaseStylingProps = (
    disabled: BaseButtonProps['disabled'],
    loading: BaseButtonProps['loading']
): ThemeTypes.BoxType => ({
    cursor: disabled ? undefined : 'pointer',
    display: 'inlineFlex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '2',
    boxSizing: 'borderBox',
    position: 'relative',
    fontWeight: 'semibold',
    userSelect: 'none',
    pointerEvents: disabled || loading ? 'none' : undefined,
    transition: `${tokens.animationDuration.fast1} ${tokens.animationTiming.easeInOut}`,
    flexShrink: 0,
    whiteSpace: 'nowrap',
});

export const getStylingProps = (
    variant: BaseButtonProps['variant'],
    loading: BaseButtonProps['loading'],
    disabled: BaseButtonProps['disabled'],
    borderColor?: BaseButtonProps['borderColor']
): ThemeTypes.BoxType => ({
    ...getBaseStylingProps(disabled, loading),
    borderWidth: 'sm',
    borderStyle: 'solid',
    borderColor,
    decoration: { default: 'none', hover: 'none' },
    ...(variant === 'primary' && {
        backgroundColor: disabled ? 'grey_1100' : 'grey_300',
    }),
    ...(variant === 'secondary' && {
        backgroundColor: disabled ? 'grey_1200' : 'grey_1100',
    }),
    ...(variant === 'tertiary' && {
        backgroundColor: 'transparent',
    }),
});

export const getClassNames = (
    variant: BaseButtonProps['variant'],
    active: BaseButtonProps['active'],
    disabled: BaseButtonProps['disabled'],
    loading: BaseButtonProps['loading']
) => ({
    [classes.baseButton]: true,
    [classes.primaryButton]: variant === 'primary',
    [classes.secondaryButton]: variant === 'secondary',
    [classes.tertiaryButton]: variant === 'tertiary',
    [classes.primaryDisabledButton]: variant === 'primary' && disabled,
    [classes.secondaryDisabledButton]: variant === 'secondary' && disabled,
    [classes.tertiaryDisabledButton]: variant === 'tertiary' && disabled,
    [classes.baseButtonActive]: active,
    [classes.baseButtonLoading]: loading,
});

export const getButtonColor = (
    variant: NonNullable<BaseButtonProps['variant']>,
    disabled?: BaseButtonProps['disabled']
) => (disabled ? BASE_BUTTON_DISABLED_COLORS[variant] : BASE_BUTTON_COLORS[variant]);
